/* eslint-disable react-hooks/exhaustive-deps */
import { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';
import { VStack, Box, Tabs } from '@chakra-ui/react';
import { FeedbackForm } from 'components/common/FeedbackForm/FeedbackForm';
import { ArticleContentSections } from 'components/Article/ArticleContentSections';
import { useIsLiked } from 'hooks/utils/useIsLiked';
import { useIsRead } from 'hooks/utils/useIsRead';
import { TabBar } from 'components/Article/TabBar';
import { ArticleHero } from 'components/Article/ArticleHero';
import { TopInformation } from 'components/Article/TopInformation';
import { ArticleSlider } from 'components/common/ArticleSlider/ArticleSlider';
import { getReadingTime } from 'utils/getReadingTime';
import { MetaSection } from 'components/Article/MetaSection';
import { AppetizerSection } from 'components/Article/AppetizerSection';
import { ArticleQuestionSections } from 'components/Article/ArticleQuestionSections';
import { LiteratureSection } from 'components/Article/LiteratureSection';
import { ProtectedRoutes } from 'types/Routes';
import { useAddLastViewedEntry } from 'hooks/api/backend/user/viewedEntries/addLastViewedEntry';
import { ArticleSliderVariants } from 'types/ArticleSliderVariants';
import { PageContext } from 'components/common/PageContext';
import {
  ContentType,
  LastViewedEntry,
  RawContentfulData,
  SubscriptionStatus,
} from '@novaheal/types';
import { InAppLayoutVariant } from 'types/Layout';
import { TabData } from 'types/tab';
import { EducationIcon, QuestionMarkIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { modifyLastViewedEntry } from 'utils/modifyLastViewedEntry';
import { useBreakpoint } from 'hooks/utils/layout/useBreakpoint';
import { Breakpoints } from 'types/breakpoints';
import { ArticleHeroDesktop } from 'components/Article/ArticleHeroDesktop';
import { useBreadcrumbs } from 'hooks/utils/useBreadcrumbs';
import { useArticleApi } from 'hooks/api/backend/useArticleApi';
import { useUser } from 'utils/auth';
import { isSubscriptionStatus } from 'utils/isSubscriptionStatus';
import { PageSpinner } from 'components/atoms/PageSpinner';
import { QueryErrorState } from 'components/common/QueryErrorState';
import { useParams } from 'react-router-dom';
import { WorksheetGeneratorSegment } from 'components/worksheet/WorksheetGeneratorSegment';
import { logBrazeEvent } from 'utils/brazeEvents';

export const Article = (): ReactElement | null => {
  const { data: user, isLoading: isUserLoading } = useUser();
  const trackedArticleIdRef = useRef<string | null>(null);
  const { id } = useParams<{ id: string }>();
  const { data: article, isError, isLoading, error, refetch } = useArticleApi(id);
  const isArticleAvailable = !!article;
  const { isLiked, handleLikeChange } = useIsLiked(
    isArticleAvailable ? article.id : '',
    ContentType.ARTICLE
  );

  const { isRead, handleReadChange } = useIsRead(isArticleAvailable ? article.id : undefined);
  const addLastViewedEntry = useAddLastViewedEntry();
  const contentSectionId = useMemo(() => window.location.hash?.slice(1), [window.location.hash]);

  const isDesktop = useBreakpoint(Breakpoints.DESKTOP);
  const { addBreadcrumbs, removeBreadcrumbs } = useBreadcrumbs();

  // constants
  const modifiedRecommendations = article?.recommendations?.map(
    (entry: RawContentfulData) => modifyLastViewedEntry(entry) as LastViewedEntry
  );
  const shouldShowRecommendations =
    !!article?.recommendations && article?.recommendations?.length > 3 && !!modifiedRecommendations;

  const trackArticleSectionOpen = useCallback(
    ({ sectionId, sectionTitle }: { sectionId?: string; sectionTitle: string }): void => {
      if (!article) return;
      logBrazeEvent('article_section_opened', {
        articleId: article.id,
        articleTitle: article.title,
        sectionId,
        sectionTitle,
      });
    },
    [article]
  );

  useEffect(() => {
    if (!article) return;
    if (trackedArticleIdRef.current === article.id) return;

    trackedArticleIdRef.current = article.id;
    logBrazeEvent('article_viewed', {
      articleId: article.id,
      articleTitle: article.title,
    });
  }, [article]);

  useEffect(() => {
    if (article) {
      addBreadcrumbs([
        {
          path: `${ProtectedRoutes.CATEGORY}/${article.category?.id}`,
          name: article.category.title,
        },
        { path: `${ProtectedRoutes.FOLDER}/${article.folder?.id}`, name: article.folder?.name },
        { path: `${ProtectedRoutes.ARTICLE}/${article.id}`, name: article.title },
      ]);
    }
    return () => {
      removeBreadcrumbs();
    };
  }, [article]);

  useEffect(() => {
    const updateView = async (): Promise<void> => {
      if (article) {
        await addLastViewedEntry.mutateAsync(article.id);
      }
    };

    updateView();
  }, [article]);

  useEffect(() => {
    if (isLoading || isUserLoading) return;
    if (isSubscriptionStatus(user, SubscriptionStatus.FREE)) return;

    // TODO: move to external fn
    const scrollToAnchorLink = (): void => {
      if (contentSectionId) {
        const element = document.getElementById(contentSectionId);
        if (element) {
          const headerOffset = isDesktop ? 120 : 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }
    };
    if (!isLoading) setTimeout(scrollToAnchorLink, 200);
  }, [contentSectionId, isDesktop, isLoading, isUserLoading]);

  if (isLoading || isUserLoading) {
    return <PageSpinner />;
  }

  if (isError) {
    return <QueryErrorState error={error} onRetry={() => refetch()} />;
  }

  //  TODO: remove the navigation bar on load as soon as we have the new layout
  if (!article) {
    return <PageSpinner />;
  }

  const readingTime = getReadingTime(article.contentSections);

  const shareData: ShareData = {
    title: article.title || 'Novaheal App',
    text: `https://app.novaheal.de${ProtectedRoutes.ARTICLE}/${article.id}?utm_source=share&utm_medium=share&utm_campaign=share_article\nLernkarte zum Thema: ${article.title}`,
    url: `${window.location.href}?utm_source=share&utm_medium=share&utm_campaign=share_article`,
  };

  const tabs: TabData[] = [
    {
      id: 0,
      icon: <NovaIcon icon={EducationIcon} size="m" aria-hidden />,
      tabName: 'Lernen',
    },
    {
      id: 1,
      icon: <NovaIcon icon={QuestionMarkIcon} size="m" aria-hidden />,
      tabName: 'Fragen',
    },
  ];

  return (
    <PageContext
      variant={InAppLayoutVariant.SECONDARY}
      handleLikeChange={() => handleLikeChange(article.title)}
      shareData={shareData}
      hasShareButton
      hasLikeButton
      isLiked={isLiked}
      hasReadButton
      handleReadChange={() => handleReadChange(article.title)}
      isRead={isRead}
      headTitle={article.title}
      title={article.title}
    >
      <WorksheetGeneratorSegment articleId={article.id} />
      <Box width="100%" my={{ md: '0px' }}>
        {isDesktop ? (
          <ArticleHeroDesktop
            coverImageUrl={article.coverImageUrl}
            title={article.title}
            readingTime={readingTime}
            ageGroups={article.ageGroups}
            relevantYears={article.relevantYears}
            tags={article.tags}
            handleLikeChange={() => handleLikeChange(article.title)}
            isLiked={isLiked}
            handleReadChange={() => handleReadChange(article.title)}
            isRead={isRead}
          />
        ) : (
          <Box width="100%" h="30vh" pt="env(safe-area-inset-top)" mb="-30px">
            <ArticleHero coverImageUrl={article.coverImageUrl} />
          </Box>
        )}
        <VStack
          width="100%"
          bg="background.primary"
          align="left"
          mt={{ base: '-30px', md: '0' }}
          pt={{ base: '50px', md: '0' }}
          gap="0"
          borderTopRadius="30px"
          boxShadow={{ base: '0px 0px 15px rgba(0, 0, 0, 0.05)', md: 'none' }}
          position="relative"
        >
          {!isDesktop && (
            <TopInformation
              isLoading={isLoading}
              parentFolder={article.folder}
              category={article.category}
              title={article.title}
              readingTime={readingTime}
              relevantYears={article.relevantYears}
              tags={article.tags}
              ageGroups={article.ageGroups}
            />
          )}
          <Box bg="background.primary" pb="50px" width="100%">
            <TabBar position="sticky" top={{ base: '50px', md: '70px' }} tabs={tabs}>
              <Tabs.Content value="Lernen" p="20px 0 0">
                <Box bg="background.primary">
                  {article.meta && <MetaSection metaData={article.meta} />}
                  <AppetizerSection appetizerData={article.appetizer} />
                  <ArticleContentSections
                    contentSections={article.contentSections}
                    contentSectionId={contentSectionId}
                    trackArticleSectionOpen={trackArticleSectionOpen}
                  />
                  <LiteratureSection literatureData={article.literature} />
                </Box>
              </Tabs.Content>
              <Tabs.Content value="Fragen" p="0" pt="l">
                {article.questionSections && (
                  <ArticleQuestionSections questionSections={article.questionSections} />
                )}
              </Tabs.Content>
            </TabBar>

            <Box m="40px" bg="background.primary">
              <FeedbackForm type={ContentType.ARTICLE} id={article.id} title={article.title} />
            </Box>

            {shouldShowRecommendations && (
              <ArticleSlider
                entries={modifiedRecommendations}
                variant={ArticleSliderVariants.RECOMMENDATION}
              />
            )}
          </Box>
        </VStack>
      </Box>
    </PageContext>
  );
};
